import { hashCanonical, hashCanonicalB64 } from './hash.js';
/**
 * Standing landing grants (docs/e2e.md "Standing landing grants").
 *
 * On a fast-profile project the user may approve ONE passkey-signed statement: "this browser
 * device may land verified work of project P into target branch B on machines M, as long as the
 * landing recipe passes, until T". While it holds, that device's `land.merge` commands carry the
 * statement + assertion instead of a fresh step-up tap (one-key land, "Land all ready"). The
 * daemon re-verifies everything per command and checks the scope against its OWN project facts
 * (project, target branch, loop profile); production releases, secrets and deletions never take
 * a grant. Plaintext-safe: ids and digests only (the branch name is bound by `targetDigest`).
 */
export const LAND_GRANT_DOMAIN = {
    statement: 'viko/land-grant/v1',
    target: 'viko/land-grant-target/v1',
};
/** Command kinds whose step-up a landing grant may stand in for. Never releases or secrets. */
export const LAND_GRANT_STEP_UP_KINDS = ['land.merge'];
/** The only policy there is: the landing recipe must pass (no "land anyway" under a grant). */
export const LAND_GRANT_POLICY = 'recipe-must-pass';
export const MAX_LAND_GRANT_TTL_MS = 30 * 24 * 60 * 60_000;
export const DEFAULT_LAND_GRANT_TTL_MS = 14 * 24 * 60 * 60_000;
export const MAX_LAND_GRANT_MACHINES = 8;
export const LAND_GRANT_CLOCK_SKEW_MS = 60_000;
export function landGrantTargetDigest(projectId, branch) {
    return hashCanonicalB64(LAND_GRANT_DOMAIN.target, { projectId, branch });
}
export async function createLandGrantStatement(input) {
    const issuedAt = input.now ?? Date.now();
    return {
        version: 1,
        type: 'viko.land-grant',
        accountId: input.accountId,
        grantId: input.grantId ?? globalThis.crypto.randomUUID(),
        projectId: input.projectId,
        targetDigest: await landGrantTargetDigest(input.projectId, input.targetBranch),
        policy: LAND_GRANT_POLICY,
        machineIds: [...new Set(input.machineIds)].sort(),
        deviceKeyId: input.deviceKeyId,
        issuedAt,
        expiresAt: issuedAt + Math.min(input.ttlMs ?? DEFAULT_LAND_GRANT_TTL_MS, MAX_LAND_GRANT_TTL_MS),
    };
}
/** WebAuthn challenge (32 bytes) for the one passkey tap that creates (or renews) a grant. */
export function createLandGrantChallenge(statement) {
    return hashCanonical(LAND_GRANT_DOMAIN.statement, statement);
}
const str = (v, max = 256) => typeof v === 'string' && v.length > 0 && v.length <= max;
const int = (v) => Number.isSafeInteger(v) && v >= 0;
const KEYS = new Set([
    'version',
    'type',
    'accountId',
    'grantId',
    'projectId',
    'targetDigest',
    'policy',
    'machineIds',
    'deviceKeyId',
    'issuedAt',
    'expiresAt',
]);
export function isLandGrantStatement(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return false;
    const s = v;
    if (Object.keys(s).some((k) => !KEYS.has(k)))
        return false;
    const ids = s.machineIds;
    return (s.version === 1 &&
        s.type === 'viko.land-grant' &&
        str(s.accountId) &&
        str(s.grantId, 128) &&
        str(s.projectId) &&
        str(s.targetDigest, 64) &&
        s.policy === LAND_GRANT_POLICY &&
        Array.isArray(ids) &&
        ids.length >= 1 &&
        ids.length <= MAX_LAND_GRANT_MACHINES &&
        ids.every((m) => str(m, 128)) &&
        new Set(ids).size === ids.length &&
        str(s.deviceKeyId) &&
        int(s.issuedAt) &&
        int(s.expiresAt));
}
/** Statement-only checks (shape, account, device, window, revocation, passkey). */
export async function verifyLandGrant(proof, trust, opts) {
    if (proof === undefined || proof === null)
        return { ok: false, reason: 'no-grant' };
    const p = proof;
    if (typeof p !== 'object' || typeof p.assertion !== 'object' || !p.assertion)
        return { ok: false, reason: 'malformed' };
    const s = p.statement;
    if (typeof s === 'object' &&
        s !== null &&
        'policy' in s &&
        s.policy !== LAND_GRANT_POLICY)
        return { ok: false, reason: 'bad-policy' };
    if (!isLandGrantStatement(s))
        return { ok: false, reason: 'malformed' };
    const now = opts.now ?? Date.now();
    const skew = opts.clockSkewMs ?? LAND_GRANT_CLOCK_SKEW_MS;
    if (s.accountId !== opts.accountId)
        return { ok: false, reason: 'wrong-account' };
    if (s.deviceKeyId !== opts.deviceKeyId)
        return { ok: false, reason: 'device-mismatch' };
    if (opts.isSessionDevice?.(s.deviceKeyId))
        return { ok: false, reason: 'session-device' };
    if (opts.machineId !== undefined && !s.machineIds.includes(opts.machineId))
        return { ok: false, reason: 'machine-not-listed' };
    if (s.expiresAt <= s.issuedAt)
        return { ok: false, reason: 'malformed', detail: 'window' };
    if (s.expiresAt - s.issuedAt > MAX_LAND_GRANT_TTL_MS)
        return { ok: false, reason: 'ttl-too-long' };
    if (s.issuedAt > now + skew)
        return { ok: false, reason: 'not-yet-valid' };
    // No skew grace on expiry: a standing grant ends when it says it ends.
    if (now >= s.expiresAt)
        return { ok: false, reason: 'expired' };
    if (opts.isRevoked?.(s.grantId))
        return { ok: false, reason: 'revoked' };
    const approval = await trust.verifyPasskeyApproval(p.assertion, await createLandGrantChallenge(s), 
    // A standing grant's one assertion is presented many times; its signCount repeats.
    {});
    if (!approval.ok)
        return { ok: false, reason: 'bad-approval', detail: approval.reason };
    return { ok: true, statement: s, credentialId: approval.credential.credentialId };
}
/** Scope checks: the grant covers this project, this target branch, and a passing recipe only. */
export async function checkLandGrantScope(statement, scope) {
    if (statement.projectId !== scope.projectId)
        return { ok: false, reason: 'project-mismatch' };
    if (statement.targetDigest !== (await landGrantTargetDigest(scope.projectId, scope.targetBranch)))
        return { ok: false, reason: 'target-mismatch' };
    if (!scope.fastLanding)
        return { ok: false, reason: 'profile-not-fast' };
    if (scope.acceptBaselineFailures)
        return { ok: false, reason: 'land-anyway-needs-passkey' };
    return { ok: true };
}
/** Whether a (decrypted) command payload carries a landing grant. */
export function payloadLandGrant(payload) {
    if (typeof payload !== 'object' || payload === null)
        return undefined;
    return payload.landGrant;
}
//# sourceMappingURL=land-grant.js.map